// Client-side LoanTape search/filter over the snapshot the Validate job writes
// (derived/<rid>/loantape.json.gz). Port of preflight.py's _search_mask,
// _filter_mask, apply_column_filters, search_rows and
// get_column_filter_options - the snapshot carries pandas' own per-cell text
// (astype(str)), blank flags and coerced numbers/dates, so results match the
// old GET .../loantape-search and .../loantape-filter-options exactly.
// Keep in sync with those functions if they change.

// pandas str.contains(query, case=False) is a case-insensitive REGEX match.
function makeMatcher(query) {
  const regex = new RegExp(query, 'i');
  return (text) => regex.test(text);
}

function searchMask(snapshot, query, column) {
  const n = snapshot.rows.length;
  const q = (query || '').trim();
  if (!q) return new Array(n).fill(true);
  const matches = makeMatcher(q);
  if (column != null) {
    const ci = snapshot.columns.indexOf(column);
    if (ci < 0) return new Array(n).fill(false);
    return snapshot.search_text.map((cells) => matches(cells[ci]));
  }
  return snapshot.search_text.map((cells) => cells.some((cell) => matches(cell)));
}

// Timestamps from _json_safe are ISO strings, naive = UTC wall time.
function toMs(iso) {
  if (iso == null) return NaN;
  const s = String(iso);
  const hasZone = /[zZ]$|[+-]\d\d:\d\d$/.test(s);
  return Date.parse(s.includes('T') && !hasZone ? `${s}Z` : s);
}

const rawValue = (snapshot, ci, r) => snapshot.rows[r][ci];

// Same equality pandas' isin() applies (5 == 5.0; strings exact).
const sameValue = (a, b) => a === b || (typeof a === 'number' && typeof b === 'number' && a === b);

function isNumericSubset(meta, indices) {
  // _infer_value_kind(subset) == "numeric": every non-blank cell coerces.
  let nonBlank = 0;
  for (const r of indices) {
    if (meta.blank[r]) continue;
    nonBlank += 1;
    if (meta.number[r] === null) return false;
  }
  return nonBlank > 0;
}

function filterMask(snapshot, filt) {
  const ci = snapshot.columns.indexOf(filt.column);
  const meta = snapshot.column_meta[filt.column];
  const n = snapshot.rows.length;
  const all = [...Array(n).keys()];
  const out = new Array(n);

  if (filt.kind === 'values') {
    const wanted = filt.values || [];
    const includeBlanks = wanted.includes(null);
    const nonNull = wanted.filter((v) => v !== null);
    const numeric = meta.value_kind === 'numeric' && isNumericSubset(meta, all);
    for (let r = 0; r < n; r += 1) {
      const value = numeric ? meta.number[r] : rawValue(snapshot, ci, r);
      const hit = nonNull.some((w) => sameValue(w, value));
      out[r] = includeBlanks ? hit || meta.blank[r] : hit && !meta.blank[r];
    }
    return out;
  }

  if (filt.kind === 'number_range') {
    const lo = filt.min == null ? -Infinity : Number(filt.min);
    const hi = filt.max == null ? Infinity : Number(filt.max);
    for (let r = 0; r < n; r += 1) {
      const v = meta.number[r];
      const hit = v !== null && v >= lo && v <= hi;
      out[r] = filt.include_blanks ? hit || meta.blank[r] : hit && !meta.blank[r];
    }
    return out;
  }

  // date_range: pandas compares against pd.to_datetime(min/max) = midnight.
  const lo = filt.min == null ? -Infinity : toMs(filt.min);
  const hi = filt.max == null ? Infinity : toMs(filt.max);
  const dates = meta.date || [];
  for (let r = 0; r < n; r += 1) {
    const t = toMs(dates[r]);
    const hit = !Number.isNaN(t) && t >= lo && t <= hi;
    out[r] = filt.include_blanks ? hit || meta.blank[r] : hit && !meta.blank[r];
  }
  return out;
}

function applyColumnFilters(snapshot, filters) {
  let mask = new Array(snapshot.rows.length).fill(true);
  for (const filt of filters || []) {
    if (!snapshot.columns.includes(filt.column)) continue;
    const m = filterMask(snapshot, filt);
    mask = mask.map((v, i) => v && m[i]);
  }
  return mask;
}

// Same shape as GET /api/preflight/{id}/loantape-search (minus added_columns,
// which the caller takes from the snapshot).
export function searchRows(snapshot, { query = '', column = null, limit = 200, filters = [] } = {}) {
  const sMask = searchMask(snapshot, query, column);
  const fMask = filters && filters.length ? applyColumnFilters(snapshot, filters) : null;
  const matched = [];
  snapshot.rows.forEach((row, i) => {
    if (sMask[i] && (!fMask || fMask[i])) matched.push(row);
  });
  const rows = matched.slice(0, limit);
  return {
    columns: snapshot.columns,
    rows,
    total_rows: snapshot.rows.length,
    preview_rows: rows.length,
    total_matches: matched.length,
    added_columns: snapshot.added_columns,
  };
}

// Python sorts option values by (isinstance(value, str), value): numbers
// (and bools) first ascending, then strings by code point.
function compareOptionValues(a, b) {
  const aStr = typeof a.value === 'string';
  const bStr = typeof b.value === 'string';
  if (aStr !== bStr) return aStr ? 1 : -1;
  if (aStr) return a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
  return Number(a.value) - Number(b.value);
}

// Same shape as GET /api/preflight/{id}/loantape-filter-options.
export function getColumnFilterOptions(snapshot, column, otherFilters = [], query = '', queryColumn = null) {
  if (!snapshot.columns.includes(column)) throw new Error(`Unknown column: ${column}`);
  const ci = snapshot.columns.indexOf(column);
  const meta = snapshot.column_meta[column];
  const kind = meta.kind;

  let mask = new Array(snapshot.rows.length).fill(true);
  for (const filt of otherFilters || []) {
    if (filt.column === column || !snapshot.columns.includes(filt.column)) continue;
    const m = filterMask(snapshot, filt);
    mask = mask.map((v, i) => v && m[i]);
  }
  const sMask = searchMask(snapshot, query, queryColumn);
  const candidates = [];
  mask.forEach((v, i) => {
    if (v && sMask[i]) candidates.push(i);
  });
  const blankCount = candidates.filter((r) => meta.blank[r]).length;

  if (kind === 'values') {
    const numeric = isNumericSubset(meta, candidates);
    const counts = new Map();
    for (const r of candidates) {
      if (meta.blank[r]) continue;
      const value = numeric ? meta.number[r] : rawValue(snapshot, ci, r);
      const key = `${typeof value}:${value}`;
      const entry = counts.get(key);
      if (entry) entry.count += 1;
      else counts.set(key, { value, count: 1 });
    }
    const values = [...counts.values()].sort(compareOptionValues);
    if (blankCount) values.push({ value: null, count: blankCount });
    return { column, kind, values, min: null, max: null, has_blanks: blankCount > 0 };
  }

  let lo = null;
  let hi = null;
  if (meta.value_kind === 'numeric') {
    for (const r of candidates) {
      const v = meta.number[r];
      if (v === null) continue;
      if (lo === null || v < lo) lo = v;
      if (hi === null || v > hi) hi = v;
    }
  } else {
    let loT = null;
    let hiT = null;
    for (const r of candidates) {
      const iso = (meta.date || [])[r];
      const t = toMs(iso);
      if (Number.isNaN(t)) continue;
      if (loT === null || t < loT) [loT, lo] = [t, iso];
      if (hiT === null || t > hiT) [hiT, hi] = [t, iso];
    }
  }
  return { column, kind, values: null, min: lo, max: hi, has_blanks: blankCount > 0 };
}

// The row for one investment_key (GET .../loantape-row's `row`), or null.
export function findRow(snapshot, investmentKey) {
  const ci = snapshot.columns.indexOf('investment_key');
  if (ci < 0) return null;
  const row = snapshot.rows.find((r) => String(r[ci]) === String(investmentKey));
  return row || null;
}
